/**
 * Helpers for table cells that arrive as Markdown from the backend.
 *
 * Only inline links are supported — `[text](url)` — which is the single
 * Markdown construct the report tables produce. Some links point inside this
 * application and some point at other Mojito apps (for example the CRM at
 * `dev.crm.empresascec.com`), so callers must route the former and let the
 * browser open the latter.
 */

export interface MarkdownLink {
  text: string;
  url: string;
}

export interface ParsedMarkdownCell {
  /** Whatever is left once every link has been taken out of the value. */
  text: string;
  links: MarkdownLink[];
}

const MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\(([^)]+)\)/g;

/**
 * Splits a cell value into its plain text and its Markdown links.
 * A value with no links comes back untouched with an empty `links` array.
 */
export function parseMarkdownLinks(value: any): ParsedMarkdownCell {
  if (!value || typeof value !== 'string') {
    return { text: value || '', links: [] };
  }

  const links: MarkdownLink[] = [];
  const regex = new RegExp(MARKDOWN_LINK_REGEX.source, 'g');
  let match: RegExpExecArray | null;

  while ((match = regex.exec(value)) !== null) {
    links.push({ text: match[1], url: match[2] });
  }

  if (links.length === 0) {
    return { text: value, links: [] };
  }

  let text = value;
  for (const link of links) {
    text = text.replace(`[${link.text}](${link.url})`, '');
  }
  text = text.replace(/[,|\s]+/g, ' ').trim();

  return { text, links };
}

/** True when the value carries at least one Markdown link. */
export function hasMarkdownLinks(value: any): boolean {
  if (!value || typeof value !== 'string') return false;
  return new RegExp(MARKDOWN_LINK_REGEX.source).test(value);
}

/**
 * Flattens a Markdown value to readable text: the leftover text followed by
 * each link label. Used where a link cannot be rendered — PDF pages, tooltips —
 * so the raw `[label](url)` never reaches the user.
 */
export function markdownToPlainText(value: any): string {
  const parsed = parseMarkdownLinks(value);
  if (parsed.links.length === 0) return parsed.text;

  return [parsed.text, ...parsed.links.map((link) => link.text)]
    .filter((part) => !!part)
    .join(' ');
}

/** True when the URL points at a different origin than the running app. */
export function isExternalUrl(url: string): boolean {
  if (!url) return false;
  try {
    return new URL(url, window.location.origin).origin !== window.location.origin;
  } catch {
    return false;
  }
}

/**
 * Router path for a same-origin link, query string and fragment included so
 * targets such as `/opportunities?opportunity=523` keep their parameters.
 */
export function toInternalPath(url: string): string {
  try {
    const parsed = new URL(url, window.location.origin);
    return `${parsed.pathname}${parsed.search}${parsed.hash}`;
  } catch {
    return url;
  }
}
